import React, { useId } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type CompareContrastData } from "@/types/models/Exportables.interface";
import { ORGANIZER_TOPIC_MAX_LENGTH } from "./CompareContrastOrganizer.constants";
import * as styles from "./CompareContrastOrganizer.styles";
import OrganizerCircleTextArea from "./OrganizerCircleTextArea";
import { updateTopic } from "./utils";

const CompareContrastOrganizer = ({
  formData,
  onChange,
  title = "Compare and Contrast",
  isStudent = false,
}: TemplateProps<CompareContrastData>) => {
  const leftTopicId = useId();
  const rightTopicId = useId();
  const data = formData as CompareContrastData;
  const leftTopic = data?.topic_pairs?.[0]?.[0] || "";
  const rightTopic = data?.topic_pairs?.[0]?.[1] || "";
  const leftSection = data?.topic_pairs?.[0]?.[2] || "";
  const rightSection = data?.topic_pairs?.[0]?.[3] || "";
  const centerSection = data?.topic_pairs?.[0]?.[4] || "";

  const handler = (
    section:
      | "left-topic"
      | "right-topic"
      | "left-section"
      | "right-section"
      | "center-section",
  ) => {
    return (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      updateTopic(section, e.target.value, onChange, formData);
  };

  const renderTopicsSection = () => (
    <div css={styles.bothSectionContainer}>
      <div css={styles.inputsRow}>
        <div css={styles.bottomLineInputWrapper}>
          {isStudent ? (
            <p css={styles.paragraphCss}>
              {leftTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
            </p>
          ) : (
            <input
              type="text"
              data-qa-selector="organizer-left-topic"
              id={leftTopicId}
              value={leftTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
              maxLength={ORGANIZER_TOPIC_MAX_LENGTH}
              onChange={handler("left-topic")}
              css={styles.bottomLineInput(
                leftTopic.length > ORGANIZER_TOPIC_MAX_LENGTH / 2,
              )}
              aria-label="Left Topic"
              placeholder="Left Topic"
            />
          )}
        </div>
        <div css={styles.bothLabel}>Both</div>
        <div css={styles.bottomLineInputWrapper}>
          {isStudent ? (
            <p css={styles.paragraphCss}>
              {rightTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
            </p>
          ) : (
            <input
              type="text"
              data-qa-selector="organizer-right-topic"
              id={rightTopicId}
              value={rightTopic.substring(0, ORGANIZER_TOPIC_MAX_LENGTH)}
              maxLength={ORGANIZER_TOPIC_MAX_LENGTH}
              onChange={handler("right-topic")}
              css={styles.bottomLineInput(
                rightTopic.length > ORGANIZER_TOPIC_MAX_LENGTH / 2,
              )}
              aria-label="Right Topic"
              placeholder="Right Topic"
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderVennDiagramSection = () => {
    return (
      <div css={styles.vennDiagramContainer}>
        <div css={styles.circleLeft} />
        <div css={styles.circleRight} />
        <div css={styles.vennDiagramTextAreaContainer(isStudent)}>
          <OrganizerCircleTextArea
            section="left"
            value={leftSection}
            onChange={handler("left-section")}
            isStudent={isStudent}
          />
          <OrganizerCircleTextArea
            section="center"
            value={centerSection}
            onChange={handler("center-section")}
            isStudent={isStudent}
          />
          <OrganizerCircleTextArea
            section="right"
            value={rightSection}
            onChange={handler("right-section")}
            isStudent={isStudent}
          />
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <ExportablesTemplateHeaderSection
      templateTitle={title}
      articleTitle={formData?.article_title}
    />
  );

  return (
    <form
      css={styles.compareContrastContainer}
      aria-label="Compare Contrast Organizer"
    >
      {renderHeader()}
      {renderTopicsSection()}
      {renderVennDiagramSection()}
    </form>
  );
};

export default CompareContrastOrganizer;
